import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import { API_ENDPOINTS } from "../../config/apiConfig";
import SageMapCanvas, { PARCEL_SHAPE_MIN_ZOOM } from "../../components/sage/SageMapCanvas";
import { useAuth } from "../../components/router/authContext";
import "./sage-map.css";

const LISTINGS_TIMEOUT_MS = 60000;
const LISTINGS_MAX_ATTEMPTS = 3;
const LIST_PAGE_SIZE = 50;

const listingsErrorMessage = (err) => {
  const status = err?.response?.status;
  if (status === 403) return "This account cannot open the Sage map.";
  if (status === 401) return "Session expired. Please sign in again.";
  if (!err?.response) return "Could not reach the listings service. Check that the backend is running, then retry.";
  return "Could not load listings.";
};

const shouldRetryListingsRequest = (err, attempt) => {
  if (attempt >= LISTINGS_MAX_ATTEMPTS - 1) return false;
  if (!err?.response) return true;
  const status = err.response.status;
  return status >= 500 || status === 408 || status === 429;
};

const FEET_PER_MILE = 5280;
const RECOMMENDED_TOP_N = 10;
const LISTING_COLOR = "#38bdf8";
const LISTING_SELECTED_COLOR = "#1d4ed8";
const RECOMMENDED_COLOR = "#b45309";
const RECOMMENDED_SELECTED_COLOR = "#7c2d12";
const PILOT_CENTER = [34.055, -117.545];

const SCORE_FEATURE_META = [
  { key: "freeway_access", label: "Freeway / ramp" },
  { key: "freight_corridor", label: "Freight corridor" },
  { key: "warehouse_density", label: "Warehouse density" },
  { key: "truck_activity", label: "Truck activity" },
  { key: "grid_readiness", label: "Grid readiness" },
  { key: "competition_whitespace", label: "Competition" },
  { key: "deal_zoning_quality", label: "Zoning fit" },
];

const ACRE_PRESETS = [
  { id: "lt1", label: "< 1", min: "", max: "1" },
  { id: "sage", label: "1–2", min: "1", max: "2" },
  { id: "2to5", label: "2–5", min: "2", max: "5" },
  { id: "gt5", label: "5+", min: "5", max: "" },
];
const PRICE_PRESETS = [
  { id: "lt6", label: "Under $6M", min: "", max: "6000000" },
  { id: "6to20", label: "$6–20M", min: "6000000", max: "20000000" },
  { id: "gt20", label: "$20M+", min: "20000000", max: "" },
];
const ASSESSED_PRESETS = [
  { id: "lt1", label: "< $1M", min: "", max: "1000000" },
  { id: "1to5", label: "$1–5M", min: "1000000", max: "5000000" },
  { id: "gt5", label: "$5M+", min: "5000000", max: "" },
];
const POWER_PRESETS = [
  { id: "ge1", label: "≥ 1 MW", min: "1", max: "" },
  { id: "ge2", label: "≥ 2 MW", min: "2", max: "" },
  { id: "ge5", label: "≥ 5 MW", min: "5", max: "" },
];
const TRUCK_PRESETS = [
  { id: "ge500", label: "≥ 500 trucks/day", min: "500", max: "" },
  { id: "ge1000", label: "≥ 1,000 trucks/day", min: "1000", max: "" },
  { id: "ge2000", label: "≥ 2,000 trucks/day", min: "2000", max: "" },
];
const RAMP_DISTANCE_PRESETS = [
  { id: "lt025", label: "< 0.25 mi", min: "", max: "0.25" },
  { id: "lt05", label: "< 0.5 mi", min: "", max: "0.5" },
  { id: "lt1", label: "< 1 mi", min: "", max: "1" },
];
const WAREHOUSE_ACRE_PRESETS = [
  { id: "ge50", label: "≥ 50 ac", min: "50", max: "" },
  { id: "ge200", label: "≥ 200 ac", min: "200", max: "" },
  { id: "ge500", label: "≥ 500 ac", min: "500", max: "" },
];
const INTERMODAL_PRESETS = [
  { id: "ge1", label: "≥ 1", min: "1", max: "" },
  { id: "ge2", label: "≥ 2", min: "2", max: "" },
];
const COMPETITION_PRESETS = [
  { id: "eq0", label: "0", min: "0", max: "0" },
  { id: "le1", label: "≤ 1", min: "", max: "1" },
  { id: "le3", label: "≤ 3", min: "", max: "3" },
];
const SORT_OPTIONS = [
  { id: "score", label: "Score" },
  { id: "price", label: "Asking price" },
  { id: "acres", label: "Acres" },
  { id: "ramp", label: "Ramp distance" },
  { id: "power", label: "Usable MW" },
];
const TYPE_PREVIEW_COUNT = 6;
const HISTOGRAM_BARS = 12;
/** Equal-width in-range bins from 0 to cap; values at or above cap go in the overflow bar. */
const ACRE_RANGE_CAP = 20;
const ACRE_HISTOGRAM_BINS = 24;
const ACRE_OPEN_MAX_LABEL = "20+";
const PRICE_RANGE_CAP = 20000000;
const PRICE_HISTOGRAM_BINS = 32;
const PRICE_OPEN_MAX_LABEL = "$20M+";
const ASSESSED_RANGE_CAP = 10000000;
const ASSESSED_HISTOGRAM_BINS = 32;
const ASSESSED_OPEN_MAX_LABEL = "$10M+";
const POWER_RANGE_CAP = 20;
const POWER_OPEN_MAX_LABEL = "20+ MW";


const num = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const truthy = (value) => value === true || String(value).toLowerCase() === "true";

const sageRankValue = (props) => {
  const rank = num(props?.sage_rank);
  if (rank == null || rank < 1) return null;
  return Math.round(rank);
};

const sageRecommendedRankValue = (props) => {
  const rank = num(props?.sage_recommended_rank);
  if (rank == null || rank < 1) return null;
  return Math.round(rank);
};

const sageScoreValue = (props) => {
  const score = num(props?.sage_score);
  if (score == null) return null;
  return score;
};

const isRecommendedSite = (props) => {
  const rank = sageRecommendedRankValue(props);
  return rank != null && rank <= RECOMMENDED_TOP_N;
};

const displayRankValue = (props) => sageRecommendedRankValue(props) ?? sageRankValue(props);

const formatScore = (value) => {
  const n = num(value);
  if (n == null) return "—";
  return n.toFixed(2);
};

const scoreFeatureValue = (props, key) => num(props?.[`sage_f_${key}`]);

const displayText = (value) => {
  if (value == null) return "";
  const text = String(value).trim();
  if (!text || text.toLowerCase() === "nan") return "";
  return text;
};

const propertyTypes = (props) =>
  String(props.property_type || "").split(",").map((part) => part.trim()).filter(Boolean);

const SQFT_PER_ACRE = 43560;
const BUILDING_ACRES_TOLERANCE = 0.02;

const looksLikeBuildingSqft = (lotSqft, buildingSqft) => {
  const lot = num(lotSqft);
  const building = num(buildingSqft);
  if (lot == null || building == null || lot <= 0 || building <= 0) return false;
  return Math.abs(lot - building) < Math.max(1, 0.01 * building);
};

const looksLikeBuildingAcres = (acres, buildingSqft) => {
  const lot = num(acres);
  const building = num(buildingSqft);
  if (lot == null || building == null || lot <= 0 || building <= 0) return false;
  return Math.abs(lot - building / SQFT_PER_ACRE) < BUILDING_ACRES_TOLERANCE;
};

/** Broker lot acres when the listing states a real lot (not building SF as acres). */
const brokerLotAcres = (props) => {
  const explicit = num(props.acreage_broker_lot);
  if (explicit != null && explicit > 0) return explicit;
  const building = props.building_sqft;
  const lotSqft = num(props.lot_size_sqft);
  if (lotSqft != null && lotSqft > 0 && !looksLikeBuildingSqft(lotSqft, building)) {
    return lotSqft / SQFT_PER_ACRE;
  }
  const listing = num(props.acreage_listing);
  if (listing != null && listing > 0 && !looksLikeBuildingAcres(listing, building)) {
    return listing;
  }
  return null;
};

const countyLotAcres = (props) => num(props.acreage_parcel_assessor || props.acreage_assessor);

/** Working acres: broker lot if available, else county assessed, else empty. */
const acreageValue = (props) => {
  const broker = brokerLotAcres(props);
  if (broker != null) return broker;
  return countyLotAcres(props);
};

const priceValue = (props) => {
  if (props.sale_price_usable === "" || props.sale_price_usable == null) {
    if (props.record_type === "listing" && props.sale_price_usable === "") return null;
  }
  const explicit = num(props.sale_price_usable);
  if (explicit != null) return explicit > 0 ? explicit : null;
  const raw = num(props.sale_price_usd || props.price);
  if (raw == null || raw < 1000 || raw > 500000000) return null;
  return raw;
};

const assessedValue = (props) => num(props.parcel_total_assessed || props.total_assessed);

const featureLabel = (props) =>
  displayText(props.address) || displayText(props.full_address) || `APN ${displayText(props.apn)}`;

const formatMoney = (value) => {
  const n = num(value);
  if (n == null || n <= 0) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
};

const formatAcres = (value) => {
  const n = num(value);
  if (n == null || n <= 0) return "—";
  return `${n.toLocaleString("en-US", { maximumFractionDigits: 2 })} ac`;
};

const formatSqft = (value) => {
  const n = num(String(value ?? "").replace(/[$,]/g, ""));
  if (n == null || n <= 0) return displayText(value);
  return `${Math.round(n).toLocaleString("en-US")} sqft`;
};

const warehouseAcresValue = (props) => {
  const sqft = num(props.warehouse_sqft_2mi);
  if (sqft == null || sqft < 0) return null;
  return sqft / SQFT_PER_ACRE;
};

const formatWarehouseAcres = (sqft) => {
  const n = num(sqft);
  if (n == null || n < 0) return "";
  return `${Math.round(n / SQFT_PER_ACRE).toLocaleString("en-US")} ac`;
};

const nearestRampMiles = (props) => {
  const feet = num(props.nearest_ramp_distance_ft);
  if (feet == null || feet < 0) return null;
  return feet / FEET_PER_MILE;
};

/** Open + under-dev + planned MDHD charging sites within 2 mi. */
const competitionSites2mi = (props) => {
  const parts = [
    num(props.ev_existing_site_count_2mi),
    num(props.ev_under_dev_site_count_2mi),
    num(props.ev_planned_site_count_2mi),
  ];
  if (parts.every((value) => value == null)) return null;
  return parts.reduce((sum, value) => sum + (value ?? 0), 0);
};

const askingPriceText = (props) => {
  const usable = priceValue(props);
  if (usable != null) return formatMoney(usable);
  if (num(props.sale_price_usd ?? props.price) == null) return "No price";
  return "Unusable";
};

const localityLine = (props) => {
  const address = featureLabel(props).toLowerCase();
  const parts = [];
  const city = displayText(props.city);
  const zip = displayText(props.zip_code);
  if (city && !address.includes(city.toLowerCase())) parts.push(city);
  if (zip && !address.includes(zip.toLowerCase())) parts.push(zip);
  return parts.join(" ");
};

const listingApns = (props) => {
  const raw = props.apns;
  if (Array.isArray(raw)) return raw.map((part) => displayText(part)).filter(Boolean);
  return String(raw || "").split("|").map((part) => part.trim()).filter(Boolean);
};

const apnText = (props) => {
  const all = listingApns(props);
  if (displayText(props.location_method) === "parcel_union" && all.length > 1) return all.join(", ");
  return displayText(props.apn) || all[0] || "";
};

const zoneText = (props) => [
  displayText(props.parcel_zone_code || props.zone_code),
  displayText(props.parcel_zone_name || props.zone_name),
  displayText(props.parcel_zoning_tier || props.zoning_tier),
].filter(Boolean).join(" · ");

const formatNumber = (value, digits = 2) => {
  const n = num(value);
  if (n == null) return "—";
  return n.toLocaleString("en-US", { maximumFractionDigits: digits });
};

const formatDistance = (feet) => {
  const n = num(feet);
  if (n == null || n < 0) return "—";
  if (n >= 5280) return `${(n / 5280).toFixed(1)} mi`;
  return `${Math.round(n).toLocaleString()} ft`;
};

const formatNamedPower = (name, capacityMw, distanceFt) => {
  if (!displayText(name)) return "";
  const parts = [displayText(name), `${formatNumber(capacityMw)} MW`];
  if (num(distanceFt) != null && num(distanceFt) >= 0) {
    parts.push(formatDistance(distanceFt));
  }
  return parts.join(" · ");
};

const CURRENT_CAPACITY_YEAR = new Date().getFullYear();

const blockYears = (block) => {
  if (!block || !Array.isArray(block.mw) || !block.mw.length) return [];
  const start = Number(block.plan_year);
  if (!Number.isFinite(start)) return [];
  return block.mw.map((_, index) => start + index);
};

const outlookYearsForFeature = (outlook) => {
  if (!outlook || typeof outlook !== "object") return [];
  const sets = ["circuit", "substation", "a_bank"]
    .map((key) => blockYears(outlook[key]))
    .filter((years) => years.length);
  if (!sets.length) return [];
  return sets.slice(1).reduce(
    (years, next) => years.filter((year) => next.includes(year)),
    sets[0],
  );
};

/** Years present on every loaded outlook, from the current year forward. */
const capacityYearOptions = (features) => {
  let shared = null;
  features.forEach((feature) => {
    const years = outlookYearsForFeature(feature.properties?.capacity_outlook);
    if (!years.length) return;
    const set = new Set(years);
    if (shared == null) shared = set;
    else shared = new Set([...shared].filter((year) => set.has(year)));
  });
  const list = (shared ? [...shared] : [])
    .filter((year) => year >= CURRENT_CAPACITY_YEAR)
    .sort((a, b) => a - b);
  return list.length ? list : [CURRENT_CAPACITY_YEAR];
};

const defaultCapacityYear = (years) => {
  if (years.includes(CURRENT_CAPACITY_YEAR)) return CURRENT_CAPACITY_YEAR;
  const earlier = years.filter((year) => year <= CURRENT_CAPACITY_YEAR);
  if (earlier.length) return earlier[earlier.length - 1];
  return years[0];
};

const outlookMw = (block, year) => {
  const start = Number(block.plan_year);
  if (!Number.isFinite(start) || !Array.isArray(block.mw)) return null;
  const index = year - start;
  if (index < 0 || index >= block.mw.length) return null;
  return num(block.mw[index]);
};

const componentMw = (props, year, kind, headlineKey) => {
  const block = props.capacity_outlook?.[kind];
  if (block) return outlookMw(block, year);
  if (year === CURRENT_CAPACITY_YEAR) return num(props[headlineKey]);
  return null;
};

/** Binding usable MW = min of circuit / substation / A-Bank for the selected outlook year. */
const usablePowerCapacity = (props, year = CURRENT_CAPACITY_YEAR) => {
  const parts = [
    ["Circuit", componentMw(props, year, "circuit", "nearest_circuit_capacity_mw")],
    ["Substation", componentMw(props, year, "substation", "nearest_substation_capacity_mw")],
    ["A-Bank", componentMw(props, year, "a_bank", "nearest_a_bank_capacity_mw")],
  ];
  const candidates = parts.filter(([, mw]) => mw != null);
  if (!candidates.length) {
    return {
      usableMw: null,
      limitedBy: "",
      circuitMw: parts[0][1],
      substationMw: parts[1][1],
      aBankMw: parts[2][1],
      year,
    };
  }
  let best = candidates[0];
  for (let i = 1; i < candidates.length; i += 1) {
    if (candidates[i][1] < best[1]) best = candidates[i];
  }
  return {
    usableMw: best[1],
    limitedBy: best[0],
    circuitMw: parts[0][1],
    substationMw: parts[1][1],
    aBankMw: parts[2][1],
    year,
  };
};

const formatTruckRoad = (road, volume) => {
  if (!displayText(road)) return "";
  const flow = formatTrafficVolume(volume, "trucks/day");
  return flow ? `${displayText(road)} · ${flow}` : displayText(road);
};

const formatMeasured = (value, digits, suffix) => {
  if (num(value) == null) return "";
  return `${formatNumber(value, digits)} ${suffix}`;
};

const STATION_TYPE_LABELS = {
  OR: "On-ramp",
  FR: "Off-ramp",
  ML: "Mainline",
};

const stationTypeLabel = (value) => STATION_TYPE_LABELS[displayText(value)] || displayText(value);

const formatTrafficVolume = (value, unit) => {
  const n = num(value);
  if (n == null) return "";
  return `${formatNumber(n, 0)} ${unit}`;
};

const formatAccessPoint = ({ freeway, direction, type, distanceFt, volume, volumeUnit }) => {
  const road = [displayText(freeway), displayText(direction)].filter(Boolean).join(" ");
  const typeLabel = stationTypeLabel(type);
  const distance = num(distanceFt) != null ? formatDistance(distanceFt) : "";
  const volumeText = formatTrafficVolume(volume, volumeUnit);
  let identity = [road, typeLabel].filter(Boolean).join(" · ");
  if (distance && identity) identity = `${identity} (${distance})`;
  else if (distance) identity = distance;
  const parts = [identity, volumeText].filter(Boolean);
  return parts.join(" · ");
};

const inRange = (value, min, max) => {
  if (min == null && max == null) return true;
  if (value == null) return false;
  if (min != null && value < min) return false;
  if (max != null && value > max) return false;
  return true;
};

const parseBound = (raw) => {
  const text = String(raw || "").trim().replace(/[$,]/g, "");
  if (!text) return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
};

const centroid = (geometry) => {
  if (!geometry) return PILOT_CENTER;
  if (geometry.type === "Point") {
    const [lon, lat] = geometry.coordinates;
    return [lat, lon];
  }
  const coords = [];
  const walk = (node) => {
    if (typeof node?.[0] === "number") coords.push(node);
    else if (Array.isArray(node)) node.forEach(walk);
  };
  walk(geometry.coordinates);
  if (!coords.length) return PILOT_CENTER;
  const lon = coords.reduce((sum, pair) => sum + pair[0], 0) / coords.length;
  const lat = coords.reduce((sum, pair) => sum + pair[1], 0) / coords.length;
  return [lat, lon];
};

const memberLinks = (props) => {
  if (Array.isArray(props.member_links) && props.member_links.length) return props.member_links;
  return [];
};

function SourceMarks({ props }) {
  const recommended = isRecommendedSite(props);
  const recRank = sageRecommendedRankValue(props);
  if (!truthy(props.meets_sage_hard_filters) && !recommended) return null;
  return (
    <div className="sage-kicker">
      {recommended ? <span className="sage-badge sage-badge-rank">#{recRank}</span> : null}
      {truthy(props.meets_sage_hard_filters) ? <span className="sage-badge">Sage</span> : null}
    </div>
  );
}

function ScoreBreakdown({ props }) {
  const score = sageScoreValue(props);
  const rank = displayRankValue(props);
  if (score == null && rank == null) return null;
  return (
    <section className="sage-group sage-score-group">
      <GroupHeading
        title="Recommendation"
        tooltip="Freight-first weighted score for Sage’s acquisition screen (freeway/ramp, freight, warehouses, truck activity, grid, competition, zoning). Every listing is scored; Recommended is the hard-filter top 10."
      />
      <div className="sage-score-head">
        {rank != null ? <span className="sage-score-rank">#{rank}</span> : null}
        <strong className="sage-score-value">{formatScore(score)}</strong>
        <span className="sage-score-label">site score</span>
      </div>
      <ul className="sage-score-bars">
        {SCORE_FEATURE_META.map((meta) => {
          const value = scoreFeatureValue(props, meta.key);
          const pct = value == null ? 0 : Math.max(0, Math.min(100, Math.round(value * 100)));
          return (
            <li key={meta.key}>
              <div className="sage-score-bar-label">
                <span>{meta.label}</span>
                <span>{value == null ? "—" : formatScore(value)}</span>
              </div>
              <div className="sage-score-bar-track" aria-hidden="true">
                <div className="sage-score-bar-fill" style={{ width: `${pct}%` }} />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function HoverCard({ props }) {
  const types = propertyTypes(props).join(" · ") || "Type not listed";
  const sub = [localityLine(props), types].filter(Boolean).join(" · ");
  const rank = displayRankValue(props);
  return (
    <div>
      <SourceMarks props={props} />
      <div className="sage-hover-title">{featureLabel(props)}</div>
      {sub ? <p className="sage-hover-sub">{sub}</p> : null}
      <div className="sage-hover-metrics">
        <div><span>Asking</span><strong>{askingPriceText(props)}</strong></div>
        <div><span>Acres</span><strong>{formatAcres(acreageValue(props))}</strong></div>
        <div><span>Assessed</span><strong>{formatMoney(assessedValue(props))}</strong></div>
        {sageScoreValue(props) != null ? (
          <div>
            <span>{rank != null ? "Rank" : "Score"}</span>
            <strong>
              {rank != null ? `#${rank} · ` : ""}
              {formatScore(sageScoreValue(props))}
            </strong>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const GROUP_TOOLTIPS = {
  "Power Grid":
    "Estimated available hosting capacity near this site. Usable capacity is the lowest of the nearest circuit, substation, and A-Bank for the selected capacity year. Higher usable capacity generally means less interconnection risk for truck charging.",
  Traffic:
    "Nearby freight movement from CalTrans: nearest highway mainline, busiest highway within 2 miles, closest freeway ramp, and busiest ramp within 2 miles. Stronger truck traffic and closer ramp access usually mean better charging demand. (Note: Highway rows show trucks/day; ramps show vehicles/day because CalTrans PeMS ramp data is not truck-split.)",
  "Other Demand Indicators":
    "Local freight activity beyond road traffic: warehouses and intermodal terminals within 2 miles. Dense warehouse and intermodal clusters signal higher potential for medium- and heavy-duty charging demand.",
  Competition:
    "Nearby medium- and heavy-duty EV charging sites within 2 miles from CalSTART — open, in development, and planned. More nearby sites mean more competition for the same truck-charging demand.",
};

function GroupHeading({ title, tooltip }) {
  if (!tooltip) {
    return (
      <h3>
        <span className="sage-group-title">{title}</span>
      </h3>
    );
  }
  const tipId = `sage-tip-${title.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <h3 className="sage-group-heading">
      <span className="sage-group-title">{title}</span>
      <span className="sage-tip-wrap">
        <button
          type="button"
          className="sage-tip-btn"
          aria-label={`About ${title}`}
          aria-describedby={tipId}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
            <circle cx="6" cy="6" r="5.25" fill="none" stroke="currentColor" strokeWidth="1.1" />
            <path d="M6 5.2v3.2M6 3.6h.01" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
        </button>
        <span id={tipId} role="tooltip" className="sage-tip">
          {tooltip}
        </span>
      </span>
    </h3>
  );
}

export function DetailCard({ feature, onClose, capacityYear = CURRENT_CAPACITY_YEAR }) {
  if (!feature) return null;
  const props = feature.properties || {};
  const links = memberLinks(props);
  const types = propertyTypes(props).join(" · ");
  const sub = [localityLine(props), types].filter(Boolean).join(" · ");
  const { usableMw, limitedBy, circuitMw, substationMw, aBankMw, year } = usablePowerCapacity(props, capacityYear);
  const groups = [
    {
      title: "Listing",
      rows: [
        ["Status", displayText(props.listing_status)],
        ["Subtype", displayText(props.property_subtype)],
        ["Building", formatSqft(props.building_sqft)],
        ["Broker", [displayText(props.broker_name), displayText(props.brokerage)].filter(Boolean).join(" · ")],
      ],
    },
    {
      title: "Parcel",
      rows: [
        ["APN", apnText(props)],
        ["Broker lot", (() => {
          const acres = brokerLotAcres(props);
          return acres != null ? formatAcres(acres) : "";
        })()],
        ["County lot", (() => {
          const acres = countyLotAcres(props);
          return acres != null ? formatAcres(acres) : "";
        })()],
        ["Zone", zoneText(props)],
        ["County", displayText(props.parcel_county || props.county)],
      ],
    },
    {
      title: "Power Grid",
      tooltip: GROUP_TOOLTIPS["Power Grid"],
      rows: [
        ["Utility", displayText(props.utility_name)],
        ["Capacity year", year != null ? String(year) : ""],
        ["Usable capacity", usableMw != null ? `${formatNumber(usableMw)} MW` : ""],
        ["Limited by", limitedBy],
        ["Circuit", formatNamedPower(props.nearest_circuit_name, circuitMw)],
        ["Substation", formatNamedPower(props.nearest_substation_name, substationMw)],
        ["A-Bank", formatNamedPower(props.nearest_a_bank_name, aBankMw)],
      ],
    },
    {
      title: "Traffic",
      tooltip: GROUP_TOOLTIPS.Traffic,
      rows: [
        ["Nearest highway", formatAccessPoint({
          freeway: props.nearest_mainline_freeway,
          direction: props.nearest_mainline_direction,
          distanceFt: props.nearest_mainline_distance_ft,
          volume: props.nearest_mainline_volume,
          volumeUnit: "trucks/day",
        })],
        ["Busiest highway 2 mi", formatTruckRoad(props.truck_top1_road_2mi, props.truck_top1_max_2mi)],
        ["Nearest ramp", formatAccessPoint({
          freeway: props.nearest_ramp_freeway,
          direction: props.nearest_ramp_direction,
          type: props.nearest_ramp_type,
          distanceFt: props.nearest_ramp_distance_ft,
          volume: props.nearest_ramp_volume,
          volumeUnit: "veh/day",
        })],
        ["Busiest ramp 2 mi", formatAccessPoint({
          freeway: props.ramp_top1_freeway_2mi,
          type: props.ramp_top1_type_2mi,
          volume: props.ramp_top1_volume_2mi,
          volumeUnit: "veh/day",
        })],
      ],
    },
    {
      title: "Other Demand Indicators",
      tooltip: GROUP_TOOLTIPS["Other Demand Indicators"],
      rows: [
        ["Warehouses", num(props.warehouse_count_2mi) != null
          ? [
            `${formatNumber(props.warehouse_count_2mi, 0)} sites`,
            formatWarehouseAcres(props.warehouse_sqft_2mi),
          ].filter(Boolean).join(" · ")
          : ""],
        ["Intermodal terminal", num(props.intermodal_count_2mi) != null
          ? formatNumber(props.intermodal_count_2mi, 0)
          : ""],
      ],
    },
    {
      title: "Competition",
      tooltip: GROUP_TOOLTIPS.Competition,
      rows: [
        ["Open sites", num(props.ev_existing_site_count_2mi) != null
          ? formatNumber(props.ev_existing_site_count_2mi, 0)
          : ""],
        ["In development sites", num(props.ev_under_dev_site_count_2mi) != null
          ? formatNumber(props.ev_under_dev_site_count_2mi, 0)
          : ""],
        ["Planned sites", num(props.ev_planned_site_count_2mi) != null
          ? formatNumber(props.ev_planned_site_count_2mi, 0)
          : ""],
      ],
    },
  ]
    .map((group) => ({ ...group, rows: group.rows.filter(([, value]) => value) }))
    .filter((group) => group.rows.length || group.note);

  return (
    <aside className="sage-detail sage-scroll">
      <button type="button" className="sage-close" onClick={onClose} aria-label="Close">
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
          <path d="M3 3l8 8M11 3 3 11" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
        </svg>
      </button>
      <h2 className="sage-detail-title">{featureLabel(props)}</h2>
      {sub ? <p className="sage-detail-sub">{sub}</p> : null}
      <div className="sage-metrics">
        <div><span>Asking</span><strong>{askingPriceText(props)}</strong></div>
        <div><span>Acres</span><strong>{formatAcres(acreageValue(props))}</strong></div>
        <div><span>Assessed</span><strong>{formatMoney(assessedValue(props))}</strong></div>
      </div>
      <ScoreBreakdown props={props} />
      {groups.map((group) => (
        <section key={group.title} className="sage-group">
          <GroupHeading title={group.title} tooltip={group.tooltip} />
          {group.rows.length ? (
            <dl>
              {group.rows.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
          {group.note ? <p className="sage-note">{group.note}</p> : null}
        </section>
      ))}
      {displayText(props.description) ? <p className="sage-copy">{displayText(props.description)}</p> : null}
      {links.length ? (
        <div className="sage-links">
          {links.map((link, index) => (
            <a key={`${link.url}-${index}`} href={link.url} target="_blank" rel="noreferrer" className="sage-link">
              {links.length > 1 ? `View online ${index + 1}` : "View online"}
            </a>
          ))}
        </div>
      ) : null}
    </aside>
  );
}

const inMapBounds = (feature, bounds) => {
  if (!bounds) return true;
  const [lat, lng] = centroid(feature.geometry);
  if (lat < bounds.south || lat > bounds.north) return false;
  if (bounds.west <= bounds.east) {
    return lng >= bounds.west && lng <= bounds.east;
  }
  return lng >= bounds.west || lng <= bounds.east;
};

const SAGE_ACRE_MIN = "1";
const SAGE_ACRE_MAX = "2";
const SAGE_PRICE_MAX = "6000000";

const emptyFilters = {
  types: null,
  acreMin: "",
  acreMax: "",
  priceMin: "",
  priceMax: "",
  assessedMin: "",
  assessedMax: "",
  powerMin: "",
  powerMax: "",
  powerYear: CURRENT_CAPACITY_YEAR,
  truckMin: "",
  truckMax: "",
  rampMin: "",
  rampMax: "",
  warehouseAcreMin: "",
  warehouseAcreMax: "",
  intermodalMin: "",
  intermodalMax: "",
  competitionMin: "",
  competitionMax: "",
  sage: false,
  priced: false,
  search: "",
  sort: "score",
};

const applySageScreen = (current, sage) => {
  if (sage) {
    return {
      ...current,
      sage: true,
      acreMin: SAGE_ACRE_MIN,
      acreMax: SAGE_ACRE_MAX,
      priceMin: "",
      priceMax: SAGE_PRICE_MAX,
    };
  }
  const next = { ...current, sage: false };
  if (current.acreMin === SAGE_ACRE_MIN && current.acreMax === SAGE_ACRE_MAX) {
    next.acreMin = "";
    next.acreMax = "";
  }
  if (!current.priceMin && current.priceMax === SAGE_PRICE_MAX) {
    next.priceMax = "";
  }
  return next;
};

const compareScore = (a, b) => {
  const scoreA = sageScoreValue(a.properties) ?? Number.NEGATIVE_INFINITY;
  const scoreB = sageScoreValue(b.properties) ?? Number.NEGATIVE_INFINITY;
  if (scoreA !== scoreB) return scoreB - scoreA;
  const rankA = sageRankValue(a.properties) ?? Number.POSITIVE_INFINITY;
  const rankB = sageRankValue(b.properties) ?? Number.POSITIVE_INFINITY;
  return rankA - rankB;
};

const compareRecommended = (a, b) => {
  const rankA = sageRecommendedRankValue(a.properties) ?? Number.POSITIVE_INFINITY;
  const rankB = sageRecommendedRankValue(b.properties) ?? Number.POSITIVE_INFINITY;
  if (rankA !== rankB) return rankA - rankB;
  return compareScore(a, b);
};

const compareNumeric = (a, b, pick, direction) => {
  const left = pick(a.properties || {});
  const right = pick(b.properties || {});
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  if (left === right) return 0;
  return direction * (left - right);
};

const sortList = (list, { sort, powerYear, listMode }) => {
  if (sort === "score" && listMode === "recommended") return [...list].sort(compareRecommended);
  if (sort === "score") return [...list].sort(compareScore);
  const pickers = {
    price: priceValue,
    acres: acreageValue,
    ramp: nearestRampMiles,
    power: (props) => usablePowerCapacity(props, powerYear).usableMw,
  };
  const direction = sort === "power" ? -1 : 1;
  const pick = pickers[sort] || priceValue;
  return [...list].sort((a, b) => compareNumeric(a, b, pick, direction) || compareScore(a, b));
};

const collectMetric = (features, pick) => {
  const values = [];
  features.forEach((feature) => {
    const value = pick(feature.properties || {});
    if (value != null && value >= 0) values.push(value);
  });
  return values;
};

const buildHistogram = (values, buckets = HISTOGRAM_BARS) => {
  if (!values.length) return { bars: [], min: null, max: null };
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (max === min) return { bars: [{ count: values.length, start: min, end: max }], min, max };
  const span = max - min;
  const counts = Array.from({ length: buckets }, () => 0);
  values.forEach((value) => {
    let index = Math.floor(((value - min) / span) * buckets);
    if (index >= buckets) index = buckets - 1;
    if (index < 0) index = 0;
    counts[index] += 1;
  });
  return {
    min,
    max,
    bars: counts.map((count, index) => ({
      count,
      start: min + (span * index) / buckets,
      end: min + (span * (index + 1)) / buckets,
    })),
  };
};

const buildEqualHistogram = (values, rangeCap, binCount) => {
  if (!binCount || binCount < 1) {
    return { bars: [], min: 0, max: rangeCap, rangeCap, overflowAt: rangeCap };
  }
  const step = rangeCap / binCount;
  const bars = Array.from({ length: binCount }, (_, index) => {
    const start = step * index;
    const end = index === binCount - 1 ? rangeCap : step * (index + 1);
    return {
      start,
      end,
      count: values.filter((value) => value >= start && value < end).length,
      overflow: false,
    };
  });
  bars.push({
    start: rangeCap,
    end: Infinity,
    count: values.filter((value) => value >= rangeCap).length,
    overflow: true,
  });
  return { bars, min: 0, max: rangeCap, rangeCap, overflowAt: rangeCap };
};

const barOverlapsRange = (bar, lo, hi) => {
  const low = lo ?? Number.NEGATIVE_INFINITY;
  const high = hi ?? Number.POSITIVE_INFINITY;
  if (bar.overflow) return high >= bar.start;
  return bar.start < high && bar.end > low;
};

const rangeIsSet = (min, max) => min !== "" || max !== "";

const chipLabelForRange = (min, max, presets, unit) => {
  const preset = presets.find((item) => item.min === min && item.max === max);
  if (preset) {
    if (unit === "ac" && !/ac/i.test(preset.label)) return `${preset.label} ac`;
    if (unit === "MW" && !preset.label.includes("MW")) return `${preset.label} MW`;
    if (unit === "mi" && !preset.label.includes("mi")) return `${preset.label} mi`;
    return preset.label;
  }
  const text = (raw) => {
    const n = parseBound(raw);
    if (n == null) return "";
    if (unit === "$") return formatMoney(n);
    const digits = unit === "ac" || unit === "MW" || unit === "mi" ? 2 : 0;
    const formatted = n.toLocaleString("en-US", { maximumFractionDigits: digits });
    return unit ? `${formatted} ${unit}` : formatted;
  };
  if (min && max) return `${text(min)}–${text(max)}`;
  if (min) return `≥ ${text(min)}`;
  if (max) return `≤ ${text(max)}`;
  return "";
};

const buildAppliedChips = (filters, defaultYear) => {
  const chips = [];
  const search = filters.search.trim();
  if (search) chips.push({ id: "search", label: search, patch: { search: "" } });
  if (filters.sage) chips.push({ id: "sage", label: "Sage screen", patch: "unsage" });
  if (filters.types) {
    [...filters.types].forEach((type) => {
      chips.push({ id: `type:${type}`, label: type, patch: { dropType: type } });
    });
  }
  const ranges = [
    ["acres", "acreMin", "acreMax", ACRE_PRESETS, "ac", filters.sage && filters.acreMin === SAGE_ACRE_MIN && filters.acreMax === SAGE_ACRE_MAX],
    ["price", "priceMin", "priceMax", PRICE_PRESETS, "$", filters.sage && !filters.priceMin && filters.priceMax === SAGE_PRICE_MAX],
    ["assessed", "assessedMin", "assessedMax", ASSESSED_PRESETS, "$", false],
    ["power", "powerMin", "powerMax", POWER_PRESETS, "MW", false],
    ["truck", "truckMin", "truckMax", TRUCK_PRESETS, "", false],
    ["ramp", "rampMin", "rampMax", RAMP_DISTANCE_PRESETS, "mi", false],
    ["warehouse", "warehouseAcreMin", "warehouseAcreMax", WAREHOUSE_ACRE_PRESETS, "ac", false],
    ["intermodal", "intermodalMin", "intermodalMax", INTERMODAL_PRESETS, "", false],
    ["competition", "competitionMin", "competitionMax", COMPETITION_PRESETS, "", false],
  ];
  ranges.forEach(([id, minKey, maxKey, presets, unit, hidden]) => {
    if (hidden || !rangeIsSet(filters[minKey], filters[maxKey])) return;
    chips.push({
      id,
      label: chipLabelForRange(filters[minKey], filters[maxKey], presets, unit),
      patch: { [minKey]: "", [maxKey]: "" },
    });
  });
  if (filters.priced) chips.push({ id: "priced", label: "Has price", patch: { priced: false } });
  if (String(filters.powerYear) !== String(defaultYear)) {
    chips.push({ id: "year", label: String(filters.powerYear), patch: { powerYear: defaultYear } });
  }
  if (filters.sort !== "score") {
    const option = SORT_OPTIONS.find((item) => item.id === filters.sort);
    chips.push({ id: "sort", label: option?.label || "Sort", patch: { sort: "score" } });
  }
  return chips;
};

const formatBoundDisplay = (raw, kind) => {
  if (raw === "" || raw == null) return "";
  const n = parseBound(raw);
  if (n == null) return String(raw);
  const digits = kind === "money" || kind === "count" ? 0 : 2;
  return n.toLocaleString("en-US", { maximumFractionDigits: digits });
};

const sanitizeBound = (raw) => {
  const text = String(raw).replace(/,/g, "").replace(/[^0-9.]/g, "");
  const parts = text.split(".");
  if (parts.length <= 1) return text;
  return `${parts[0]}.${parts.slice(1).join("")}`;
};

const trimNumber = (value, digits) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return String(Number(n.toFixed(digits)));
};

export default function SageMapPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [features, setFeatures] = useState([]);
  const [status, setStatus] = useState("Loading listings…");
  const [error, setError] = useState("");
  const [filters, setFilters] = useState(emptyFilters);
  const [selectedId, setSelectedId] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [mapBounds, setMapBounds] = useState(null);
  const [listOpen, setListOpen] = useState(true);
  const [listPage, setListPage] = useState(1);
  const [listMode, setListMode] = useState("all"); // all | recommended
  const [typesExpanded, setTypesExpanded] = useState(false);
  const [openGroups, setOpenGroups] = useState({ grid: false, freight: false, competition: false });
  const mapRef = useRef(null);
  const featureIndexRef = useRef(new Map());

  const retryListings = useCallback(() => {
    setError("");
    setStatus("Loading listings…");
    setReloadToken((current) => current + 1);
  }, []);

  useEffect(() => {
    if (authLoading) return undefined;
    if (!isAuthenticated) {
      setFeatures([]);
      setStatus("");
      setError("Session expired. Please sign in again.");
      return undefined;
    }

    let cancelled = false;
    const token = localStorage.getItem("authToken");
    if (!token) {
      setFeatures([]);
      setStatus("");
      setError("Session expired. Please sign in again.");
      return undefined;
    }

    const loadListings = async (attempt = 0) => {
      try {
        const response = await axios.get(API_ENDPOINTS.sageListings, {
          headers: { Authorization: `Token ${token}` },
          timeout: LISTINGS_TIMEOUT_MS,
        });
        if (cancelled) return;
        const next = response.data?.features || [];
        setFeatures(next);
        setStatus(`${next.length} listings`);
        setError("");
      } catch (err) {
        if (cancelled) return;
        if (shouldRetryListingsRequest(err, attempt)) {
          await new Promise((resolve) => window.setTimeout(resolve, 1000 * (attempt + 1)));
          if (!cancelled) await loadListings(attempt + 1);
          return;
        }
        setFeatures([]);
        setError(listingsErrorMessage(err));
        setStatus("");
      }
    };

    setStatus("Loading listings…");
    setError("");
    loadListings();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, reloadToken]);

  useEffect(() => {
    const index = new Map();
    features.forEach((feature) => {
      const id = feature.properties?.record_id;
      if (id) index.set(id, feature);
    });
    featureIndexRef.current = index;
  }, [features]);

  const typeCounts = useMemo(() => {
    const counts = new Map();
    features.forEach((feature) => {
      const typesForFeature = propertyTypes(feature.properties || {});
      if (!typesForFeature.length) {
        counts.set("(unspecified)", (counts.get("(unspecified)") || 0) + 1);
        return;
      }
      typesForFeature.forEach((type) => {
        counts.set(type, (counts.get(type) || 0) + 1);
      });
    });
    return counts;
  }, [features]);

  const knownTypes = useMemo(() => {
    const list = Array.from(typeCounts.keys())
      .filter((type) => type !== "(unspecified)")
      .sort();
    if (typeCounts.has("(unspecified)")) list.push("(unspecified)");
    return list;
  }, [typeCounts]);

  const capacityYears = useMemo(() => capacityYearOptions(features), [features]);
  const defaultYear = defaultCapacityYear(capacityYears);

  useEffect(() => {
    if (capacityYears.includes(filters.powerYear)) return;
    setFilters((current) => ({ ...current, powerYear: defaultCapacityYear(capacityYears) }));
  }, [capacityYears, filters.powerYear]);

  const visible = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    const filtered = features.filter((feature) => {
      const props = feature.properties || {};
      const types = propertyTypes(props);
      if (filters.types && filters.types.size) {
        const selected = filters.types;
        const typeOk = !types.length ? selected.has("(unspecified)") : types.some((type) => selected.has(type));
        if (!typeOk) return false;
      }
      if (!inRange(acreageValue(props), parseBound(filters.acreMin), parseBound(filters.acreMax))) return false;
      if (!inRange(priceValue(props), parseBound(filters.priceMin), parseBound(filters.priceMax))) return false;
      if (!inRange(assessedValue(props), parseBound(filters.assessedMin), parseBound(filters.assessedMax))) return false;
      if (!inRange(usablePowerCapacity(props, filters.powerYear).usableMw, parseBound(filters.powerMin), parseBound(filters.powerMax))) return false;
      if (!inRange(num(props.truck_top1_max_2mi), parseBound(filters.truckMin), parseBound(filters.truckMax))) return false;
      if (!inRange(nearestRampMiles(props), parseBound(filters.rampMin), parseBound(filters.rampMax))) return false;
      if (!inRange(warehouseAcresValue(props), parseBound(filters.warehouseAcreMin), parseBound(filters.warehouseAcreMax))) return false;
      if (!inRange(num(props.intermodal_count_2mi), parseBound(filters.intermodalMin), parseBound(filters.intermodalMax))) return false;
      if (!inRange(competitionSites2mi(props), parseBound(filters.competitionMin), parseBound(filters.competitionMax))) return false;
      if (filters.sage && !truthy(props.meets_sage_hard_filters)) return false;
      if (filters.priced && priceValue(props) == null) return false;
      if (listMode === "recommended" && !isRecommendedSite(props)) return false;
      if (!query) return true;
      const hay = [props.address, props.city, props.apn, props.broker_name, props.brokerage, props.property_type, props.member_sources]
        .join(" ")
        .toLowerCase();
      return hay.includes(query);
    });

    return sortList(filtered, { sort: filters.sort, powerYear: filters.powerYear, listMode });
  }, [features, filters, listMode]);

  const recommendedCount = useMemo(
    () => features.filter((feature) => isRecommendedSite(feature.properties || {})).length,
    [features],
  );

  useEffect(() => {
    if (selectedId && !visible.some((feature) => feature.properties?.record_id === selectedId)) {
      setSelectedId(null);
    }
  }, [visible, selectedId]);

  useEffect(() => {
    setListPage(1);
  }, [filters, features, listMode]);

  const selected = visible.find((feature) => feature.properties?.record_id === selectedId) || null;
  const inViewVisible = useMemo(
    () => visible.filter((feature) => inMapBounds(feature, mapBounds)),
    [visible, mapBounds],
  );
  const listed = visible;

  const listPageCount = Math.max(1, Math.ceil(listed.length / LIST_PAGE_SIZE));
  const safeListPage = Math.min(listPage, listPageCount);
  const listPageItems = useMemo(() => {
    const start = (safeListPage - 1) * LIST_PAGE_SIZE;
    return listed.slice(start, start + LIST_PAGE_SIZE);
  }, [listed, safeListPage]);
  const listRangeStart = listed.length ? (safeListPage - 1) * LIST_PAGE_SIZE + 1 : 0;
  const listRangeEnd = Math.min(safeListPage * LIST_PAGE_SIZE, listed.length);

  const rankedTypes = useMemo(() => knownTypes
    .filter((type) => type !== "(unspecified)")
    .sort((a, b) => {
      const delta = (typeCounts.get(b) || 0) - (typeCounts.get(a) || 0);
      if (delta) return delta;
      return a.localeCompare(b);
    }), [knownTypes, typeCounts]);
  const hasUnspecified = knownTypes.includes("(unspecified)");
  const typeOverflow = rankedTypes.length > TYPE_PREVIEW_COUNT
    || (hasUnspecified && rankedTypes.length + 1 > TYPE_PREVIEW_COUNT);
  const shownTypes = typesExpanded || !typeOverflow ? rankedTypes : rankedTypes.slice(0, TYPE_PREVIEW_COUNT);
  const showUnspecified = hasUnspecified && (typesExpanded || !typeOverflow);

  const metricPack = useMemo(() => {
    const powerPick = (props) => usablePowerCapacity(props, filters.powerYear).usableMw;
    return {
      acres: buildEqualHistogram(collectMetric(features, acreageValue), ACRE_RANGE_CAP, ACRE_HISTOGRAM_BINS),
      price: buildEqualHistogram(collectMetric(features, priceValue), PRICE_RANGE_CAP, PRICE_HISTOGRAM_BINS),
      assessed: buildEqualHistogram(collectMetric(features, assessedValue), ASSESSED_RANGE_CAP, ASSESSED_HISTOGRAM_BINS),
      power: buildHistogram(collectMetric(features, powerPick)),
      truck: buildHistogram(collectMetric(features, (props) => num(props.truck_top1_max_2mi))),
      ramp: buildHistogram(collectMetric(features, nearestRampMiles)),
      warehouse: buildHistogram(collectMetric(features, warehouseAcresValue)),
      intermodal: buildHistogram(collectMetric(features, (props) => num(props.intermodal_count_2mi))),
      competition: buildHistogram(collectMetric(features, competitionSites2mi)),
    };
  }, [features, filters.powerYear]);

  const appliedChips = useMemo(() => buildAppliedChips(filters, defaultYear), [filters, defaultYear]);
  const summaryFor = (ids) => appliedChips.filter((chip) => ids.includes(chip.id)).map((chip) => chip.label).join(" · ");

  const toggleType = (type) => {
    setFilters((current) => {
      const types = new Set(current.types || []);
      if (types.has(type)) types.delete(type);
      else types.add(type);
      return { ...current, types: types.size ? types : null };
    });
  };

  const clearChip = (chip) => {
    setFilters((current) => {
      if (chip.patch === "unsage") return applySageScreen(current, false);
      if (chip.patch.dropType) {
        const types = new Set(current.types || []);
        types.delete(chip.patch.dropType);
        return { ...current, types: types.size ? types : null };
      }
      return { ...current, ...chip.patch };
    });
  };

  const toggleGroup = (id) => {
    setOpenGroups((current) => ({ ...current, [id]: !current[id] }));
  };

  const selectFeature = (feature) => {
    const id = feature.properties?.record_id;
    setSelectedId(id);
    const fullFeature = featureIndexRef.current.get(id) || feature;
    const map = mapRef.current;
    const center = centroid(fullFeature.geometry);
    if (!map) return;
    const latLng = { lat: center[0], lng: center[1] };
    const minZoom = fullFeature.geometry?.type === "Point" ? 14 : PARCEL_SHAPE_MIN_ZOOM;
    if ((map.getZoom() ?? 0) < minZoom) map.setZoom(minZoom);
    map.panTo(latLng);
  };

  return (
    <div className={`sage-shell${listOpen ? " is-list-open" : " is-list-collapsed"}`}>
      <aside className="sage-panel">
        <div className="sage-stats">
          <div><span>Matching</span><strong>{visible.length}</strong></div>
          <div><span>In view</span><strong>{inViewVisible.length}</strong></div>
        </div>
        <div className="sage-applied" aria-label="Applied filters">
          <div className="sage-applied-chips">
            {appliedChips.map((chip) => (
              <button
                key={chip.id}
                type="button"
                className="sage-applied-chip"
                aria-label={`Remove ${chip.label}`}
                onClick={() => clearChip(chip)}
              >
                <span>{chip.label}</span>
                <span aria-hidden="true">×</span>
              </button>
            ))}
          </div>
          <span className="sage-clear-all-slot">
            <button
              type="button"
              className={`sage-reset sage-clear-all${appliedChips.length ? "" : " is-reserved"}`}
              aria-hidden={!appliedChips.length}
              tabIndex={appliedChips.length ? 0 : -1}
              disabled={!appliedChips.length}
              onClick={() => setFilters({ ...emptyFilters })}
            >
              Clear all
            </button>
          </span>
        </div>
        <div className={`sage-status${error ? " is-error" : ""}`}>
          <p>{error || status}</p>
          <span className="sage-retry-slot">
            <button
              type="button"
              className={`sage-retry${error ? "" : " is-reserved"}`}
              aria-hidden={!error}
              tabIndex={error ? 0 : -1}
              disabled={!error}
              onClick={retryListings}
            >
              Retry
            </button>
          </span>
        </div>
        <div className="sage-filters sage-scroll">
          <section className="sage-section">
            <div className="sage-section-head">
              <h2>Property type</h2>
            </div>
            <div className="sage-chips">
              {shownTypes.map((type) => (
                <button
                  key={type}
                  type="button"
                  aria-pressed={Boolean(filters.types?.has(type))}
                  className={`sage-chip${filters.types?.has(type) ? " is-on" : ""}`}
                  onClick={() => toggleType(type)}
                >
                  {type}
                </button>
              ))}
              {showUnspecified ? (
                <button
                  type="button"
                  aria-pressed={Boolean(filters.types?.has("(unspecified)"))}
                  className={`sage-chip is-quiet${filters.types?.has("(unspecified)") ? " is-on" : ""}`}
                  onClick={() => toggleType("(unspecified)")}
                >
                  (unspecified)
                </button>
              ) : null}
              {typeOverflow ? (
                <button
                  type="button"
                  className="sage-chip"
                  aria-expanded={typesExpanded}
                  onClick={() => setTypesExpanded((open) => !open)}
                >
                  {typesExpanded ? "Less" : "More"}
                </button>
              ) : null}
            </div>
          </section>
          <RangeGroup
            label="Acres"
            kind="acres"
            suffix="ac"
            min={filters.acreMin}
            max={filters.acreMax}
            presets={ACRE_PRESETS}
            histogram={metricPack.acres}
            rangeCap={ACRE_RANGE_CAP}
            openMaxLabel={ACRE_OPEN_MAX_LABEL}
            onChange={(acreMin, acreMax) => setFilters((current) => ({ ...current, acreMin, acreMax }))}
          />
          <RangeGroup
            label="Asking price"
            kind="money"
            prefix="$"
            min={filters.priceMin}
            max={filters.priceMax}
            presets={PRICE_PRESETS}
            histogram={metricPack.price}
            rangeCap={PRICE_RANGE_CAP}
            openMaxLabel={PRICE_OPEN_MAX_LABEL}
            onChange={(priceMin, priceMax) => setFilters((current) => ({ ...current, priceMin, priceMax }))}
          />
          <RangeGroup
            label="Assessed value"
            kind="money"
            prefix="$"
            min={filters.assessedMin}
            max={filters.assessedMax}
            presets={ASSESSED_PRESETS}
            histogram={metricPack.assessed}
            rangeCap={ASSESSED_RANGE_CAP}
            openMaxLabel={ASSESSED_OPEN_MAX_LABEL}
            onChange={(assessedMin, assessedMax) => setFilters((current) => ({ ...current, assessedMin, assessedMax }))}
          />
          <section className="sage-section">
            <div className="sage-flags">
              <Flag
                label="Has a usable asking price"
                checked={filters.priced}
                onChange={(priced) => setFilters((current) => ({ ...current, priced }))}
              />
            </div>
          </section>
          <div className="sage-accordions">
            <FilterAccordion
              id="grid"
              title="Power Grid"
              hint={(
                <div className="sage-hint sage-hint-legend">
                  <p>Estimated hosting capacity near the site.</p>
                  <ul>
                    <li><strong>Capacity year</strong> — which year&apos;s circuit, substation, and A-Bank outlook to use.</li>
                    <li><strong>Usable capacity</strong> — the binding MW limit: the lowest of the nearest circuit, substation, and A-Bank for that year.</li>
                  </ul>
                </div>
              )}
              open={openGroups.grid}
              summary={summaryFor(["power", "year"])}
              onToggle={() => toggleGroup("grid")}
            >
              <RangeGroup
                label="Usable capacity"
                sublabel=""
                kind="mw"
                suffix="MW"
                min={filters.powerMin}
                max={filters.powerMax}
                presets={POWER_PRESETS}
                domain={metricPack.power}
                rangeCap={POWER_RANGE_CAP}
                openMaxLabel={POWER_OPEN_MAX_LABEL}
                leading={(
                  <div className="sage-chips sage-year-chips" role="group" aria-label="Capacity year">
                    {capacityYears.map((year) => (
                      <button
                        key={year}
                        type="button"
                        aria-pressed={filters.powerYear === year}
                        className={`sage-chip${filters.powerYear === year ? " is-on" : ""}`}
                        onClick={() => setFilters((current) => ({ ...current, powerYear: year }))}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                )}
                onChange={(powerMin, powerMax) => setFilters((current) => ({ ...current, powerMin, powerMax }))}
              />
            </FilterAccordion>
            <FilterAccordion
              id="freight"
              title="Freight & access"
              open={openGroups.freight}
              summary={summaryFor(["truck", "ramp", "warehouse", "intermodal"])}
              onToggle={() => toggleGroup("freight")}
            >
              <RangeGroup
                label="Busiest highway (2 mi)"
                sublabel="Peak daily trucks on the highest-volume freeway segment within 2 mi · CalTrans PeMS"
                kind="count"
                min={filters.truckMin}
                max={filters.truckMax}
                presets={TRUCK_PRESETS}
                domain={metricPack.truck}
                onChange={(truckMin, truckMax) => setFilters((current) => ({ ...current, truckMin, truckMax }))}
              />
              <RangeGroup
                label="Nearest ramp"
                sublabel="Straight-line distance to the closest freeway on/off ramp"
                kind="miles"
                suffix="mi"
                min={filters.rampMin}
                max={filters.rampMax}
                presets={RAMP_DISTANCE_PRESETS}
                domain={metricPack.ramp}
                onChange={(rampMin, rampMax) => setFilters((current) => ({ ...current, rampMin, rampMax }))}
              />
              <RangeGroup
                label="Warehouse acres"
                sublabel="Summed warehouse lot area within 2 mi"
                kind="acres"
                suffix="ac"
                min={filters.warehouseAcreMin}
                max={filters.warehouseAcreMax}
                presets={WAREHOUSE_ACRE_PRESETS}
                domain={metricPack.warehouse}
                onChange={(warehouseAcreMin, warehouseAcreMax) => setFilters((current) => ({ ...current, warehouseAcreMin, warehouseAcreMax }))}
              />
              <RangeGroup
                label="Intermodal terminals"
                sublabel="Rail/intermodal terminal count within 2 mi"
                kind="count"
                min={filters.intermodalMin}
                max={filters.intermodalMax}
                presets={INTERMODAL_PRESETS}
                domain={metricPack.intermodal}
                onChange={(intermodalMin, intermodalMax) => setFilters((current) => ({ ...current, intermodalMin, intermodalMax }))}
              />
            </FilterAccordion>
            <FilterAccordion
              id="competition"
              title="Competition"
              hint="Open, in development, and planned MDHD charging sites within 2 miles."
              open={openGroups.competition}
              summary={summaryFor(["competition"])}
              onToggle={() => toggleGroup("competition")}
            >
              <RangeGroup
                label="MDHD sites"
                kind="count"
                min={filters.competitionMin}
                max={filters.competitionMax}
                presets={COMPETITION_PRESETS}
                domain={metricPack.competition}
                onChange={(competitionMin, competitionMax) => setFilters((current) => ({ ...current, competitionMin, competitionMax }))}
              />
            </FilterAccordion>
          </div>
          <div className="sage-screen-wrap">
            <button
              type="button"
              className={`sage-screen${filters.sage ? " is-on" : ""}`}
              aria-pressed={filters.sage}
              aria-label="Sage screen"
              onClick={() => setFilters((current) => applySageScreen(current, !current.sage))}
            >
              <strong>Sage screen</strong>
              <small>1–2 ac · under $6M</small>
            </button>
          </div>
        </div>
        <div className="sage-panel-foot">
          <strong>{listed.length === 1 ? "1 site" : `${listed.length} sites`}</strong>
        </div>
      </aside>
      <div className="sage-stage">
        <SageMapCanvas
          visibleFeatures={visible}
          allFeatures={features}
          selectedId={selectedId}
          onSelectFeature={selectFeature}
          mapRef={mapRef}
          listingColor={LISTING_COLOR}
          listingSelectedColor={LISTING_SELECTED_COLOR}
          recommendedColor={RECOMMENDED_COLOR}
          recommendedSelectedColor={RECOMMENDED_SELECTED_COLOR}
          truthy={truthy}
          HoverCard={HoverCard}
          onMapBoundsChange={setMapBounds}
        />
        <DetailCard feature={selected} capacityYear={filters.powerYear} onClose={() => setSelectedId(null)} />
        {!listOpen ? (
          <button
            type="button"
            className="sage-list-toggle"
            onClick={() => setListOpen(true)}
            aria-expanded={false}
            aria-controls="sage-listings-panel"
          >
            {listMode === "recommended" ? "Recommended" : "Listings"}
            <span className="sage-list-toggle-count">{listed.length}</span>
          </button>
        ) : null}
      </div>
      <aside
        id="sage-listings-panel"
        className={`sage-list-rail${listOpen ? " is-open" : ""}`}
        aria-hidden={!listOpen}
        inert={!listOpen ? true : undefined}
      >
        <div className="sage-list">
          <div className="sage-list-head">
            <h2>{listMode === "recommended" ? "Recommended" : "Listings"}</h2>
            <div className="sage-list-head-actions">
              <span>
                {listed.length
                  ? `${listRangeStart}–${listRangeEnd} of ${listed.length}`
                  : "0"}
              </span>
              <button
                type="button"
                className="sage-list-collapse"
                onClick={() => setListOpen(false)}
                aria-label="Collapse listings"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                  <path d="M5.5 3.5 9 7l-3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
          <div className="sage-list-tabs" role="tablist" aria-label="Listing views">
            <button
              type="button"
              role="tab"
              aria-selected={listMode === "all"}
              className={`sage-list-tab${listMode === "all" ? " is-on" : ""}`}
              onClick={() => setListMode("all")}
            >
              All
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={listMode === "recommended"}
              className={`sage-list-tab${listMode === "recommended" ? " is-on" : ""}`}
              onClick={() => setListMode("recommended")}
            >
              Recommended
              <span className="sage-list-tab-count">{recommendedCount}</span>
            </button>
          </div>
          <label className="sage-sort">
            <span>Sort</span>
            <select
              aria-label="Sort"
              value={filters.sort}
              onChange={(event) => setFilters((current) => ({ ...current, sort: event.target.value }))}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
            </select>
          </label>
          <input
            type="search"
            value={filters.search}
            placeholder="Address, city, APN, broker"
            className="sage-search"
            onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
          />
          <ul className="sage-rows sage-scroll">
            {listPageItems.map((feature) => {
              const props = feature.properties || {};
              const id = props.record_id;
              const recRank = sageRecommendedRankValue(props);
              const score = sageScoreValue(props);
              const showRecBadge = recRank != null && recRank <= RECOMMENDED_TOP_N;
              return (
                <li key={id}>
                  <button
                    type="button"
                    className={id === selectedId ? "is-selected" : ""}
                    onClick={() => selectFeature(feature)}
                  >
                    <span className="sage-row-title">
                      {showRecBadge ? <span className="sage-row-rank">#{recRank}</span> : null}
                      {featureLabel(props)}
                    </span>
                    <span className="sage-row-meta">
                      {score != null ? `${formatScore(score)} · ` : ""}
                      {formatMoney(priceValue(props))} · {formatAcres(acreageValue(props))}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {listMode === "recommended" && !listed.length ? (
            <p className="sage-list-empty">No recommended sites in the current filters. Clear filters or switch to All.</p>
          ) : null}
          {listed.length > LIST_PAGE_SIZE ? (
            <div className="sage-pager">
              <button
                type="button"
                className="sage-pager-btn"
                disabled={safeListPage <= 1}
                onClick={() => setListPage((current) => Math.max(1, Math.min(current, listPageCount) - 1))}
              >
                Previous
              </button>
              <span className="sage-pager-status">
                Page {safeListPage} of {listPageCount}
              </span>
              <button
                type="button"
                className="sage-pager-btn"
                disabled={safeListPage >= listPageCount}
                onClick={() => setListPage((current) => Math.min(listPageCount, Math.min(current, listPageCount) + 1))}
              >
                Next
              </button>
            </div>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

function FilterAccordion({ id, title, hint = "", open, summary, onToggle, children }) {
  const panelId = `sage-acc-${id}`;
  return (
    <section className={`sage-accordion${open ? " is-open" : ""}`}>
      <button
        type="button"
        className="sage-accordion-toggle"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span>{title}</span>
        <span className="sage-accordion-meta">
          <em
            className={`sage-accordion-summary${summary ? "" : " is-reserved"}`}
            aria-hidden={!summary}
          >
            {summary || "—"}
          </em>
          <svg className="sage-accordion-chevron" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
            <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>
      <div id={panelId} className="sage-accordion-body" hidden={!open}>
        {hint ? (
          typeof hint === "string" ? <p className="sage-hint">{hint}</p> : hint
        ) : null}
        {children}
      </div>
    </section>
  );
}

function BoundField({
  value,
  onChange,
  kind,
  prefix,
  suffix,
  placeholder,
  label,
  openMaxLabel = "",
  showOpenMax = false,
}) {
  const [focused, setFocused] = useState(false);
  let shown = focused ? (value ?? "") : formatBoundDisplay(value, kind);
  if (!focused && showOpenMax && value === "") shown = openMaxLabel;
  return (
    <label className="sage-field">
      {prefix && !(showOpenMax && !focused && value === "") ? <span className="sage-field-affix">{prefix}</span> : null}
      <input
        aria-label={label}
        inputMode="decimal"
        placeholder={placeholder}
        value={shown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(event) => onChange(sanitizeBound(event.target.value))}
      />
      {suffix && !(showOpenMax && !focused && value === "") ? <span className="sage-field-affix">{suffix}</span> : null}
    </label>
  );
}

function Histogram({ bars, min, max }) {
  if (!bars?.length) return null;
  const peak = Math.max(...bars.map((bar) => bar.count), 1);
  const lo = parseBound(min);
  const hi = parseBound(max);
  return (
    <div className="sage-hist" aria-hidden="true">
      {bars.map((bar, index) => {
        const active = barOverlapsRange(bar, lo, hi);
        const height = Math.max(bar.count ? 18 : 6, Math.round((bar.count / peak) * 100));
        return (
          <span
            key={index}
            className={`${active ? "is-on" : ""}${bar.overflow ? " is-overflow" : ""}`}
            style={{ height: `${height}%` }}
            title={bar.overflow ? "+" : undefined}
          />
        );
      })}
    </div>
  );
}

function DualSlider({ label, min, max, domain, digits, onChange, rangeCap = null }) {
  const cap = rangeCap ?? domain?.rangeCap ?? domain?.max;
  if (!domain || domain.min == null || cap == null || cap <= domain.min) return null;
  const span = cap - domain.min;
  const currentMin = parseBound(min);
  const currentMax = parseBound(max);
  const openMax = max === "" && min !== "";
  const left = currentMin == null ? domain.min : Math.min(Math.max(currentMin, domain.min), cap);
  const right = openMax || currentMax == null
    ? cap
    : Math.min(Math.max(currentMax, domain.min), cap);
  const start = Math.min(left, right);
  const end = Math.max(left, right);
  const startPct = ((start - domain.min) / span) * 100;
  const endPct = ((end - domain.min) / span) * 100;
  const commit = (which, raw) => {
    const n = Number(raw);
    if (which === "min") {
      const next = Math.min(n, end);
      const open = Math.abs(next - domain.min) <= span * 0.002;
      onChange(open ? "" : trimNumber(next, digits), max);
      return;
    }
    const next = Math.max(n, start);
    const open = Math.abs(next - cap) <= span * 0.002;
    onChange(min, open ? "" : trimNumber(next, digits));
  };
  return (
    <div className="sage-slider">
      <div className="sage-slider-track" />
      <div className="sage-slider-fill" style={{ left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%` }} />
      <input
        type="range"
        aria-label={`${label} slider minimum`}
        min={domain.min}
        max={cap}
        step="any"
        value={start}
        onChange={(event) => commit("min", event.target.value)}
      />
      <input
        type="range"
        aria-label={`${label} slider maximum`}
        min={domain.min}
        max={cap}
        step="any"
        value={end}
        onChange={(event) => commit("max", event.target.value)}
      />
    </div>
  );
}

function RangeGroup({
  label,
  sublabel = "",
  min,
  max,
  presets,
  onChange,
  leading = null,
  kind = "count",
  prefix = "",
  suffix = "",
  domain = null,
  histogram = null,
  rangeCap = null,
  openMaxLabel = "",
}) {
  const digits = kind === "money" || kind === "count" ? 0 : 2;
  const sliderDomain = histogram?.min != null ? histogram : domain;
  const cap = rangeCap ?? sliderDomain?.rangeCap ?? sliderDomain?.max;
  const dirty = rangeIsSet(min, max);
  const showOpenMax = max === "" && min !== "" && Boolean(openMaxLabel);
  return (
    <section className="sage-section">
      <div className="sage-section-head">
        <h2>{label}</h2>
        <span className="sage-section-clear-slot">
          <button
            type="button"
            className={`sage-reset${dirty ? "" : " is-reserved"}`}
            aria-label={`Clear ${label}`}
            aria-hidden={!dirty}
            tabIndex={dirty ? 0 : -1}
            disabled={!dirty}
            onClick={() => onChange("", "")}
          >
            Clear
          </button>
        </span>
      </div>
      {sublabel ? <p className="sage-section-sublabel">{sublabel}</p> : null}
      {leading}
      {histogram?.bars?.length ? <Histogram bars={histogram.bars} min={min} max={max} /> : null}
      <DualSlider
        label={label}
        min={min}
        max={max}
        domain={sliderDomain}
        digits={digits}
        rangeCap={cap}
        onChange={onChange}
      />
      <div className="sage-range">
        <BoundField
          label={`${label} minimum`}
          kind={kind}
          prefix={prefix}
          suffix={suffix}
          placeholder="Min"
          value={min}
          onChange={(next) => onChange(next, max)}
        />
        <span className="sage-range-dash">–</span>
        <BoundField
          label={`${label} maximum`}
          kind={kind}
          prefix={prefix}
          suffix={suffix}
          placeholder="Max"
          value={max}
          openMaxLabel={openMaxLabel}
          showOpenMax={showOpenMax}
          onChange={(next) => onChange(min, next)}
        />
      </div>
      <div className="sage-chips">
        {presets.map((preset) => {
          const active = min === preset.min && max === preset.max;
          return (
            <button
              key={preset.id}
              type="button"
              aria-pressed={active}
              className={`sage-chip${active ? " is-on" : ""}`}
              onClick={() => onChange(active ? "" : preset.min, active ? "" : preset.max)}
            >
              {preset.label}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function Flag({ label, checked, onChange }) {
  return (
    <label className="sage-flag">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>{label}</span>
    </label>
  );
}
